import { z } from "zod";

/** Length of a handle, a verifier, and an S256 challenge — 32 bytes as base64url. */
export const PKCE_VALUE_LENGTH = 43;

/**
 * The exact shape of every value the native sign-in hand-off moves: 32 random
 * bytes rendered base64url, or the base64url SHA-256 of one.
 *
 * Tighter than RFC 7636's `43..128`, which we can afford because we also write
 * the client that mints the verifier. Anything else is a bug or a probe, and
 * rejecting it on shape keeps malformed input away from the database entirely.
 */
export const PKCE_VALUE_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * The `/base` query parameter by which an app asks for its sign-in to end on an
 * HTTPS callback rather than on `okthx://`. Android sends it: Chrome's Auth Tab
 * returns an HTTPS redirect only to the app verified for that host, and hands
 * the result straight back to the app that opened it.
 */
export const NATIVE_CALLBACK_PARAM = "native_callback";

/** The one value {@link NATIVE_CALLBACK_PARAM} takes. */
export const NATIVE_HTTPS_CALLBACK = "https";
export type NativeCallback = typeof NATIVE_HTTPS_CALLBACK;

/**
 * Where an HTTPS-callback sign-in ends, on the web origin. The Auth Tab stops
 * at this URL without loading it; the page exists for when it opens anywhere
 * else.
 */
export const NATIVE_HTTPS_CALLBACK_PATH = "/auth/native-callback";

export const nativeSignInFinalizeReq = z.object({
  /** The `?sign_in=` handle the callback put on the redirect back to the app. */
  handle: z.string().regex(PKCE_VALUE_PATTERN),
  /** The verifier behind the challenge sent to `/base`. Never leaves the device until now. */
  verifier: z.string().regex(PKCE_VALUE_PATTERN),
});
export type NativeSignInFinalizeReq = z.infer<typeof nativeSignInFinalizeReq>;

/**
 * The session is delivered as a `Set-Cookie`. The token is repeated in the body
 * only because the app writes its own copy of the cookie to work around a
 * fresh-install race where the response cookie isn't yet visible to
 * `CookieManager.get`; the field exists to serve that workaround and has no
 * other caller.
 */
export const nativeSignInFinalizeResp = z.object({ token: z.string() });
export type NativeSignInFinalizeResp = z.infer<typeof nativeSignInFinalizeResp>;
